import { useCallback, useEffect, useMemo, useState } from 'react';

import { useUnit } from 'effector-react/ssr';

import { useRouter } from 'next/router';

import { AccordionFilters, CheckboxFilter, ProductsListFilters } from '@domains/products-catalog';

import { $productsList, setFilters } from '@stores/products-list';

import { NewProductsPageSlug, PromoPageSlug } from '@utils/constants';
import { KopecksIso } from '@utils/isomorph';
import { makeProductListQuery } from '@utils/products-list/query';
import { makeCategoryRoute, makeSearchRoute } from '@utils/route';

export const useProductListFilters = () => {
    const setFiltersFn = useUnit(setFilters);
    const { filters, availableFilters, searchQuery, categorySlug, sortMode, originalFilters } =
        useUnit($productsList);

    const [checkboxFilters, setCheckboxFilters] = useState<CheckboxFilter[]>([]);
    const [accordionFilters, setAccordionFilters] = useState<AccordionFilters>({
        countries: [],
        manufacturers: [],
    });

    const isCategory = searchQuery === null && categorySlug !== null;

    const router = useRouter();

    const isPromoPage = router.asPath.includes(makeCategoryRoute(PromoPageSlug));
    const isNewProductsPage = router.asPath.includes(makeCategoryRoute(NewProductsPageSlug));

    const formatAccordionFilters = useCallback(
        (
            filtersList: string[],
            selectedFilters: string[] | undefined,
            isCountries: boolean,
        ): CheckboxFilter[] =>
            filtersList.map((item) => {
                const checkedFilters = selectedFilters === undefined ? [] : selectedFilters;
                const isSelected = checkedFilters.includes(item);

                return {
                    isSelected,
                    label: item,
                    onSelect: (value: boolean) => {
                        const newCheckedFilters = value
                            ? [...checkedFilters, item]
                            : checkedFilters.filter((filter) => item !== filter);

                        if (isCountries) {
                            setFiltersFn({
                                ...filters,
                                countries: newCheckedFilters,
                            });
                        } else {
                            setFiltersFn({
                                ...filters,
                                manufacturers: newCheckedFilters,
                            });
                        }
                    },
                };
            }),
        [filters, setFiltersFn],
    );

    const applyFilters = useCallback(
        (customFilters?: ProductsListFilters) => {
            router.push(
                {
                    pathname: isCategory ? makeCategoryRoute(categorySlug) : makeSearchRoute(),
                    query: makeProductListQuery({
                        searchQuery,
                        sortMode,
                        filters: customFilters !== undefined ? customFilters : filters,
                    }),
                },
                undefined,
                { scroll: false },
            );
        },
        [categorySlug, filters, isCategory, router, searchQuery, sortMode],
    );

    const discardFilters = () => {
        router.push(
            {
                pathname: isCategory ? makeCategoryRoute(categorySlug) : makeSearchRoute(),
                query: makeProductListQuery({
                    searchQuery,
                    sortMode,
                }),
            },
            undefined,
            { scroll: false },
        );
    };

    const filtersChips = useMemo(() => {
        let result = [];
        const {
            is_novelty,
            is_ready_to_eat,
            is_promo,
            min_price,
            max_price,
            countries,
            manufacturers,
        } = originalFilters;

        if (is_ready_to_eat) {
            result.push({
                id: 'is_ready_to_eat',
                label: 'Готов к употреблению',
                onClick: () => applyFilters({ ...filters, is_ready_to_eat: false }),
            });
        }

        if (!isNewProductsPage && is_novelty) {
            result.push({
                id: 'is_novelty',
                label: 'Новинки',
                onClick: () => applyFilters({ ...filters, is_novelty: false }),
            });
        }

        if (!isPromoPage && is_promo) {
            result.push({
                id: 'is_promo',
                label: 'Акции',
                onClick: () => applyFilters({ ...filters, is_promo: false }),
            });
        }

        if (min_price !== undefined || max_price !== undefined) {
            const priceRangeChipCaptionParts = [
                min_price !== undefined ? `от ${KopecksIso.to(min_price)}` : null,
                max_price !== undefined ? `до ${KopecksIso.to(max_price)}` : null,
            ]
                .filter((item) => item !== null)
                .join(' ');

            const priceRangeChipCaption = `Цена: ${priceRangeChipCaptionParts}`;

            result.push({
                id: 'price',
                label: priceRangeChipCaption,
                onClick: () =>
                    applyFilters({ ...filters, min_price: undefined, max_price: undefined }),
            });
        }

        if (countries !== undefined && countries.length > 0) {
            result = [
                ...result,
                ...countries.map((item) => ({
                    id: `countries-${item}`,
                    label: item,
                    onClick: () =>
                        applyFilters({
                            ...filters,
                            countries: countries.filter((country) => country !== item),
                        }),
                })),
            ];
        }

        if (manufacturers !== undefined && manufacturers.length > 0) {
            result = [
                ...result,
                ...manufacturers.map((item) => ({
                    id: `manufacturers-${item}`,
                    label: item,
                    onClick: () =>
                        applyFilters({
                            ...filters,
                            manufacturers: manufacturers.filter(
                                (manufacture) => manufacture !== item,
                            ),
                        }),
                })),
            ];
        }

        return result;
    }, [isPromoPage, isNewProductsPage, applyFilters, filters, originalFilters]);

    useEffect(() => {
        const availableCheckboxFilters = [];

        if (availableFilters.is_ready_to_eat) {
            availableCheckboxFilters.push({
                onSelect: (value: boolean) => setFiltersFn({ ...filters, is_ready_to_eat: value }),
                label: 'Готов к употреблению',
                isSelected: Boolean(filters.is_ready_to_eat),
            });
        }

        if (!isNewProductsPage && availableFilters.is_novelty) {
            availableCheckboxFilters.push({
                onSelect: (value: boolean) => setFiltersFn({ ...filters, is_novelty: value }),
                label: 'Новинки',
                isSelected: Boolean(filters.is_novelty),
            });
        }

        if (!isPromoPage && availableFilters.is_promo) {
            availableCheckboxFilters.push({
                onSelect: (value: boolean) => setFiltersFn({ ...filters, is_promo: value }),
                label: 'Акции',
                isSelected: Boolean(filters.is_promo),
            });
        }

        setAccordionFilters({
            countries: formatAccordionFilters(availableFilters.countries, filters.countries, true),
            manufacturers: formatAccordionFilters(
                availableFilters.manufacturers,
                filters.manufacturers,
                false,
            ),
        });
        setCheckboxFilters(availableCheckboxFilters);
    }, [
        isPromoPage,
        isNewProductsPage,
        availableFilters,
        filters,
        formatAccordionFilters,
        setFiltersFn,
    ]);

    return { checkboxFilters, accordionFilters, applyFilters, discardFilters, filtersChips };
};
